"use client";

import type { ComponentType, SVGProps } from "react";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { Button as RACButton, Dialog, DialogTrigger, Popover } from "react-aria-components";

import { cn } from "@ui/lib/utils";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { buttonVariants } from "../core/Button";
import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconCirclePlus from "@ui/central-icons/icons/IconCirclePlus";
import IconCircleMinus from "@ui/central-icons/icons/IconCircleMinus";
import IconClapperboard from "@ui/central-icons/icons/IconClapperboard";
import IconSoundFx from "@ui/central-icons/icons/IconSoundFx";
import IconDices from "@ui/central-icons/icons/IconDices";

/** Minimal shape required to render the menu and initiate a download. */
export type SfxDownloadMenuItem = { id: string; title: string; audioUrl?: string };

/** Edit options shown as menu rows in the default lineup. */
export const SFX_EDIT_OPTION_IDS = ["longer", "shorter", "cinematic", "reverb", "surprise"] as const;
export type SfxEditOptionId = (typeof SFX_EDIT_OPTION_IDS)[number];

const OPTION_ICONS: Record<SfxEditOptionId, ComponentType<SVGProps<SVGSVGElement>>> = {
  longer: IconCirclePlus,
  shorter: IconCircleMinus,
  cinematic: IconClapperboard,
  reverb: IconSoundFx,
  surprise: IconDices,
};

/**
 * Modifier keys that "Surprise me" picks from at random. Each maps to a
 * `modifier_${key}` entry in the SoundEffectsDownloadMenu translation block.
 */
export const SFX_SURPRISE_MODIFIER_KEYS = [
  "loop_seamless",
  "punchier",
  "softer",
  "intense",
  "darker",
  "brighter",
  "dreamy",
  "lofi",
  "eight_bit",
  "gritty",
  "closer",
  "distant",
] as const;
export type SfxSurpriseModifierKey = (typeof SFX_SURPRISE_MODIFIER_KEYS)[number];

/** A single row in the menu: a leading icon + label that runs an action and closes the popover. */
function SfxMenuItem({
  icon: Icon,
  label,
  onPress,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  onPress: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onPress}
      className={clsx(
        "tw-flex tw-w-full tw-items-center tw-gap-3 tw-whitespace-nowrap tw-rounded-xl tw-px-2.5 tw-py-2 tw-text-left",
        "tw-transition-colors hover:tw-bg-cream",
        "tw-outline-none focus-visible:tw-bg-cream focus-visible:tw-outline-focus",
      )}
    >
      <Icon className={clsx(iconStrokeFix, "tw-size-[18px] tw-shrink-0 tw-text-black/55")} />
      <span className='tw-type-sm tw-text-black'>{label}</span>
    </button>
  );
}

interface SfxDownloadMenuProps {
  item: SfxDownloadMenuItem;
  /** Performs the actual download (the "Download without modification" action). */
  onDownload: (item: SfxDownloadMenuItem) => void;
  /** Pre-fills the on-page prompt box with an AI-edit modifier for the item. */
  onEdit: (item: SfxDownloadMenuItem, optionId: SfxEditOptionId) => void;
  /** Notified when the menu opens/closes (used by callers for analytics). */
  onOpenChange?: (isOpen: boolean) => void;
}

function SfxDownloadMenu({ item, onDownload, onEdit, onOpenChange }: SfxDownloadMenuProps) {
  const t = useTranslations("ClientKeys.SoundEffectsDownloadMenu");
  const tGlobals = useTranslations("ClientKeys.Globals");

  return (
    <DialogTrigger onOpenChange={onOpenChange}>
      <RACButton
        aria-label={tGlobals("download")}
        className={cn(
          // Reuse the shared ghost-warm download button styling (color, rounding, hover, focus)…
          buttonVariants({ variant: "ghost-warm", size: null }),
          // …and keep the list's responsive sizing: square icon on mobile, icon + label on desktop.
          "tw-h-10 tw-w-10 tw-gap-x-3 tw-px-0 sm:tw-w-auto sm:tw-px-4",
        )}
      >
        <IconArrowInbox className={clsx(iconStrokeFix, "tw-size-5")} />
        <span className='tw-hidden tw-type-sm sm:tw-inline'>{tGlobals("download")}</span>
      </RACButton>

      <Popover
        placement='bottom right'
        offset={8}
        className={({ isEntering, isExiting }) =>
          clsx(
            "tw-z-[9999] tw-min-w-[240px] tw-rounded-[18px] tw-border-0.5 tw-border-solid tw-border-black/[0.06] tw-bg-white tw-p-1.5",
            "tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_8px_24px_0px_rgba(0,0,0,0.08),0px_2px_4px_0px_rgba(0,0,0,0.04)]",
            isEntering && "tw-animate-in tw-fade-in tw-zoom-in-95 tw-duration-150",
            isExiting && "tw-animate-out tw-fade-out tw-zoom-out-95 tw-duration-150",
          )
        }
      >
        <Dialog className='tw-flex tw-flex-col tw-outline-none'>
          {({ close }) => (
            <>
              <p className='tw-px-2.5 tw-pb-1.5 tw-pt-2 tw-type-xs tw-text-gray-400'>{t("customize_label")}</p>

              {SFX_EDIT_OPTION_IDS.map(id => (
                <SfxMenuItem
                  key={id}
                  icon={OPTION_ICONS[id]}
                  label={t(`label_${id}`)}
                  onPress={() => {
                    onEdit(item, id);
                    close();
                  }}
                />
              ))}

              <div className='tw-mx-2 tw-my-1.5 tw-h-px tw-bg-gray-100' />

              <SfxMenuItem
                icon={IconArrowInbox}
                label={t("download_as_is")}
                onPress={() => {
                  onDownload(item);
                  close();
                }}
              />
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

SfxDownloadMenu.displayName = "SfxDownloadMenu";

export { SfxDownloadMenu };
export type { SfxDownloadMenuProps };
